import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http'
import {environment} from '@environments/environment';
import {Observable, tap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';

type TempAuth = { email: string, password: string };

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _idToken: string;
	private _temp: TempAuth;
	private _accessToken: string;

  // inactivity timer
  private mouseMoveListener: EventListener;
  private clickListener: EventListener;
  private keydownListener: EventListener;
  private lastActivityTimestamp: number;
  private inactivityTimer: any;
  private hasShownInactivityWarning = false;
  private readonly inactivityTimeout = 15 * 60 * 1000; // 15 minutes inactivity timer

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private http: HttpClient) {
  }

  get idToken() { return this._idToken; }

	get accessToken() { return this._accessToken; }

	get temp(): TempAuth { return this._temp; }

  set idToken(token: string) { this._idToken = token }

	set accesToken(token: string) { this._accessToken = token }

	set temp(auth: TempAuth) { this._temp = auth; }

  signup(payload: any): Observable<any> {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/sign-up', payload);
  }

  forceResetPassword(payload: any): Observable<any> {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/change-password', payload, { params: { 'no-token': 'true' }}).pipe(
      tap((res: any) => {
        if (res.body.id_token) {
          this._idToken = res.body.id_token;
        }
      })
    );
  }

  forceResetPasswordMassive(payload: any): Observable<any> {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/force-change-password-massive', payload, { params: { 'no-token': 'true' }}).pipe(
      tap((res: any) => {
        if (res.body.id_token) {
          this._idToken = res.body.id_token;
        }
      })
    );
  }

	changePassword(old_password: string, new_password: string, access_token: string): Observable<any> {
		const payload = { old_password, new_password, access_token};
		return this.http.post(environment.AUTH_API_URL + '/auth/v1/account/change-password', payload);
	}

  setCookie(name: string, token: string) {
    document.cookie = `${name}=${token}`;
  }

  signin(payload: any): Observable<any> {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/sign-in', payload, { params: { 'no-token': 'true'} }).pipe(
      tap((res: any) => {
        if (res.body.id_token) {
          this._idToken = res.body.id_token;
					this._accessToken = res.body.access_token;
        }
      })
    );
  }

  confirmUser(payload: any) {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/confirm-username', payload);
  }

  resendCode(payload: any) {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/send-confirm-username', payload);
  }

  forgotPassword(payload: any) {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/forgot-password', payload);
  }

  resetPassword(payload: any) {
    return this.http.post(environment.AUTH_API_URL + '/v1/account/reset-password', payload);
  }

	clearTemp() {
		this.temp = { email: '', password: '' };
	}

  logout() {
    this._idToken = '';
    document.cookie = 'sessionToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    document.cookie = 'accessToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    sessionStorage.clear();
		this.clearTemp();
    this.router.navigate(['']);
    // inactivity
    clearInterval(this.inactivityTimer);
    window.removeEventListener('mousemove', this.mouseMoveListener);
    window.removeEventListener('click', this.clickListener);
    window.removeEventListener('keydown', this.keydownListener);
  }

  // inactivity

  initInactivityTimer() {
    this.updateActivityTimestamp();
    this.setupEventListeners();
    this.setupInactivityTimer();
  }

  updateActivityTimestamp() {
    this.hasShownInactivityWarning = false;
    this.lastActivityTimestamp = Date.now();
  }

  setupEventListeners() {
    this.mouseMoveListener = () => this.updateActivityTimestamp();
    this.clickListener = () => this.updateActivityTimestamp();
    this.keydownListener = () => this.updateActivityTimestamp();
    window.addEventListener('mousemove', this.mouseMoveListener);
    window.addEventListener('click', this.clickListener);
    window.addEventListener('keydown', this.keydownListener);
  }

  setupInactivityTimer() {
    this.inactivityTimer = setInterval(() => {
      const currentTime = Date.now();
      const inactiveDuration = currentTime - this.lastActivityTimestamp;
      const remainingTime = this.inactivityTimeout - inactiveDuration;
      const remainingMinutes = Math.ceil(remainingTime / (60 * 1000));
      if (remainingMinutes === 1 && !this.hasShownInactivityWarning) {
        const message: string = this.translate.instant('messages.session_inactivity_timeout');
        this.toastr.info(message);
        this.hasShownInactivityWarning = true;
      }
      if (inactiveDuration > this.inactivityTimeout) {
        const message: string = this.translate.instant('messages.session_closed_inactivity');
        this.toastr.info(message);
        this.logout();
      }
    }, 1000);
  }


}
